import { colors, COLORS } from '@constants/colors';

import { editorScrollbar, PANEL_BG, PANEL_BLUR, PANEL_BORDER } from '../styles';

// The editor's dialog text colour. A shade off pure white, which is what the editor's other
// destructive confirmations use — panel rows use COLORS.WHITE, dialogs use this.
const DIALOG_TEXT = '#e2e4ed';

export const useInspectorStyles = () => ({
  section: {
    marginTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  sectionLabel: {
    color: colors.white_50,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '0 4px',
  },

  fieldLabel: {
    color: colors.white_60,
    fontSize: '12px',
    fontWeight: 500,
    padding: '0 4px',
    marginBottom: '4px',
  },
  // Inside a card the padding is already there, and the card's own gap spaces
  // the label from its control.
  fieldCardLabel: {
    color: colors.white_60,
    fontSize: '12px',
    fontWeight: 500,
  },

  // A panel that owns its own scrolling: the fields scroll, the action bar does
  // not. Sticky would have been fewer lines but the bar then overlays the
  // scrollport, and no backdrop over this translucent glass hides the rows
  // travelling underneath it.
  panelColumn: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  panelScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    // Bottom padding, not a margin on the last field: the gap has to belong to
    // the scrolling pane so the final row clears the action bar's border.
    padding: '0 10px 14px',
    ...editorScrollbar,
  },
  panelActions: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
  },

  // Explicit disabled colours: MUI's default contained-disabled is a near-black
  // fill with 30%-white text, which on this panel reads as an empty slab.
  saveButton: {
    textTransform: 'none',
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    color: COLORS.WHITE,
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    padding: '9px 14px',
    boxShadow: 'none',
    gap: '6px',
    '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.10)',
      color: colors.white_60,
    },
  },
  deleteButton: {
    textTransform: 'none',
    color: '#ff8585',
    borderColor: 'rgba(255, 255, 255, 0.18)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    padding: '8px 14px',
    gap: '6px',
    '&:hover': {
      borderColor: '#FF6B6B',
      backgroundColor: 'rgba(255, 107, 107, 0.06)',
    },
    '&.Mui-disabled': {
      color: colors.white_60,
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
  },

  // One card per field group, the same surface the hotspot panel uses for its
  // media block and its Active / Interactive rows. Flat labels over flat inputs
  // ran together into one wall once a type carried four properties.
  fieldCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '10px 12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.10)',
  },
  fieldCardStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },

  // Inline label-and-switch row. The surrounding card supplies the background,
  // so this only lays the two out.
  propertyToggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
  },
  propertyToggleLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: COLORS.WHITE,
  },

  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      borderRadius: '8px',
      color: COLORS.WHITE,
      fontSize: '13px',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.18)' },
      '&.Mui-focused fieldset': { borderColor: COLORS.MYTAVERSE_BLUE },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 10px',
    },
    // MUI's default label color is dark and unreadable against this panel's
    // dark background until the field is focused (focused label picks up
    // the theme's primary color, which happens to read fine here).
    '& .MuiInputLabel-root': {
      color: colors.white_60,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: COLORS.MYTAVERSE_BLUE,
    },
  },

  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '6px 8px',
    borderRadius: '8px',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
    },
  },
  colorSwatch: {
    width: 26,
    height: 26,
    borderRadius: '6px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'transform 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      transform: 'scale(1.04)',
      borderColor: 'rgba(255, 255, 255, 0.32)',
    },
  },
  colorRowText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  colorRowLabel: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 500,
  },
  colorRowHex: {
    color: colors.white_50,
    fontSize: '11px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },

  pickerPopover: {
    backgroundColor: 'rgba(20, 20, 24, 0.97)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(16px)',
    borderRadius: '12px',
    padding: '12px',
  },
  // The editor's own dropdown surface: the same dark ground as pickerPopover without its padding,
  // which a MenuList brings itself. A Menu given no paper at all is the case this exists to stop —
  // the app theme sets typography and nothing else, so MUI's default light Paper renders as a white
  // panel out of a dark one, and the item states go with it: `Mui-selected` and hover are rgba
  // *black* overlays, invisible on this ground, so both are restated rather than inherited.
  menuPopover: {
    backgroundColor: 'rgba(20, 20, 24, 0.97)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(16px)',
    borderRadius: '12px',
    marginTop: '6px',
    // The entrance menu lists the whole kit, so it scrolls rather than growing past the panel.
    maxHeight: '340px',
    color: COLORS.WHITE,
    // Doubled on purpose. MUI styles `Mui-selected` on the item itself, so a single-`&` descendant
    // rule from the paper ties with it on specificity and the winner comes down to emotion's
    // injection order; `&&` puts this class in the selector twice and settles it.
    '&& .MuiMenuItem-root': {
      fontSize: '12px',
      color: COLORS.WHITE,
    },
    '&& .MuiMenuItem-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
    '&& .MuiMenuItem-root.Mui-selected': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&& .MuiDivider-root': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
      margin: '4px 0',
    },
    '&& .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.14)',
    },
  },
  // The editor's destructive-confirmation surface, built from the panel tokens rather than from a
  // fresh copy of their values. The child selectors are here because MUI's Dialog parts carry the
  // theme's own text colour, which is near-black: without them the title and body of a dark dialog
  // are unreadable.
  confirmDialogPaper: {
    background: PANEL_BG,
    backdropFilter: PANEL_BLUR,
    WebkitBackdropFilter: PANEL_BLUR,
    border: PANEL_BORDER,
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
    borderRadius: '16px',
    // The editor's dialog text is a shade off pure white, unlike its panel rows — kept verbatim so
    // this reads as the same question as the AI-script and knowledge confirmations, which still
    // carry their own copies of these values.
    color: DIALOG_TEXT,
    '& .MuiDialogTitle-root': { color: DIALOG_TEXT },
    '& .MuiDialogContentText-root': { color: DIALOG_TEXT },
    '& .MuiButton-text': { color: DIALOG_TEXT },
    '& .MuiButton-textError': { color: '#f06d6d' },
  },
  pickerHexField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      borderRadius: '8px',
      color: COLORS.WHITE,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '13px',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.18)' },
      '&.Mui-focused fieldset': { borderColor: COLORS.MYTAVERSE_BLUE },
    },
    '& .MuiOutlinedInput-input': {
      padding: '6px 10px',
      textAlign: 'center',
    },
  },

  helperError: {
    color: '#FF6B6B',
    fontSize: '11px',
    padding: '0 4px',
  },
  helperHint: {
    color: 'rgba(255, 255, 255, 0.62)',
    fontSize: '11px',
    padding: '0 4px',
  },
  helperWarning: {
    color: '#f0b357',
    fontSize: '11px',
    padding: '0 4px',
  },
  assignScriptButton: {
    marginTop: '8px',
    textTransform: 'none',
    fontSize: '12px',
    borderColor: 'rgba(255, 255, 255, 0.18)',
    color: 'rgba(255, 255, 255, 0.85)',
    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.35)' },
    '&.Mui-disabled': {
      borderColor: 'rgba(255, 255, 255, 0.08)',
      color: 'rgba(255, 255, 255, 0.35)',
    },
  },

  spotPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  spotThumb: {
    width: 44,
    height: 44,
    borderRadius: '8px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white_40,
    position: 'relative',
  },
  spotThumbClickable: {
    cursor: 'zoom-in',
    transition: 'transform 0.15s ease',
    '&:hover': {
      transform: 'scale(1.04)',
    },
    '&:hover .spot-thumb-expand': {
      opacity: 1,
    },
  },
  spotThumbExpandHint: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    color: COLORS.WHITE,
    opacity: 0,
    transition: 'opacity 0.15s ease',
    pointerEvents: 'none',
  },
  spotThumbVideoBadge: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    color: COLORS.WHITE,
    pointerEvents: 'none',
  },
  spotThumbPlaceholder: {
    background:
      'repeating-linear-gradient(45deg, rgba(58,58,70,1) 0px, rgba(58,58,70,1) 8px, rgba(46,46,58,1) 8px, rgba(46,46,58,1) 16px)',
  },
  spotFilename: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  spotFilenameText: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  spotFilenameMeta: {
    color: colors.white_50,
    fontSize: '11px',
  },

  mediaCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '10px 12px',
    borderRadius: '14px',
    backgroundColor: 'rgba(255, 255, 255, 0.045)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
  },

  compactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  compactThumb: {
    width: 44,
    height: 44,
    borderRadius: '10px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white_40,
    position: 'relative',
  },
  compactInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  compactTitle: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  compactSubtitle: {
    color: colors.white_50,
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  compactActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  iconSquareButton: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: COLORS.WHITE,
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.32)',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
    },
  },
  iconDangerButton: {
    '&:hover': {
      borderColor: '#FF6B6B',
      color: '#FF6B6B',
      backgroundColor: 'rgba(255, 107, 107, 0.06)',
    },
  },
  compactDivider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    margin: '2px 0',
  },
  thumbExpandBadge: {
    position: 'absolute',
    bottom: 3,
    right: 3,
    width: 16,
    height: 16,
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    color: COLORS.WHITE,
    pointerEvents: 'none',
  },
  progressBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  progressTextRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: 14,
  },
  compactProgressBar: {
    height: 3,
    borderRadius: '99px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '& .MuiLinearProgress-bar': {
      borderRadius: '99px',
      backgroundColor: COLORS.MYTAVERSE_BLUE,
    },
  },
  previewStatusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minWidth: 0,
  },
  previewStatusOk: {
    color: '#4CCBA0',
  },
  previewStatusRequired: {
    color: '#FF6B6B',
  },
  uploadErrorText: {
    color: '#FF6B6B',
    fontSize: '11px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  uploadErrorBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    border: '1px solid rgba(255, 107, 107, 0.32)',
    borderRadius: '8px',
    padding: '6px 10px',
  },
  uploadErrorBannerText: {
    color: '#FF6B6B',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  cancelUploadButton: {
    marginLeft: 'auto',
    fontSize: '11px',
    fontWeight: 600,
    color: '#AEB6FF',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': { textDecoration: 'underline' },
  },
  mediaThumb: {
    width: 84,
    height: 68,
    borderRadius: '12px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.white_40,
    position: 'relative',
  },
  mediaThumbVideo: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  mediaInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    flex: 1,
    minWidth: 0,
  },
  mediaTitle: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mediaSubtitle: {
    color: colors.white_50,
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mediaActionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
  },
  mediaActionButton: {
    flex: 1,
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    textTransform: 'none',
    padding: '5px 8px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    minWidth: 0,
    gap: '4px',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
    '& .MuiButton-startIcon': {
      marginRight: '4px',
      marginLeft: 0,
    },
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.72)',
      borderColor: 'rgba(255, 255, 255, 0.16)',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      opacity: 1,
    },
  },
  mediaProgressRow: {
    display: 'grid',
    gridTemplateColumns: '13px minmax(0, 1fr) 58px',
    alignItems: 'center',
    gap: '7px',
    minHeight: 18,
  },
  mediaProgress: {
    height: 3,
    borderRadius: '99px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '& .MuiLinearProgress-bar': {
      borderRadius: '99px',
      backgroundColor: COLORS.MYTAVERSE_BLUE,
    },
  },

  spotActionsRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '4px',
  },
  spotActionButton: {
    flex: 1,
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    textTransform: 'none',
    padding: '6px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    gap: '4px',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.72)',
      borderColor: 'rgba(255, 255, 255, 0.16)',
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      opacity: 1,
    },
  },
  spotDeleteButton: {
    minWidth: 36,
    flex: '0 0 36px',
    padding: '6px',
    color: colors.white_60,
    borderColor: 'rgba(255, 255, 255, 0.18)',
    '&:hover': {
      borderColor: '#FF6B6B',
      color: '#FF6B6B',
      backgroundColor: 'rgba(255, 107, 107, 0.06)',
    },
  },

  segmented: {
    display: 'flex',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '8px',
    padding: '3px',
    gap: '2px',
  },
  segmentedItem: {
    flex: 1,
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.white_60,
    cursor: 'pointer',
    borderRadius: '6px',
    textAlign: 'center',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      color: COLORS.WHITE,
    },
  },
  segmentedItemActive: {
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    color: COLORS.WHITE,
    '&:hover': {
      color: COLORS.WHITE,
    },
  },
  segmentedItemDisabled: {
    cursor: 'not-allowed',
    opacity: 0.4,
    pointerEvents: 'none',
  },
  segmentedItemLocked: {
    cursor: 'default',
    pointerEvents: 'none',
  },

  conversionRow: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 64px',
    alignItems: 'center',
    gap: '10px',
    padding: '5px 8px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  conversionText: {
    color: colors.white_60,
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  identifierField: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px',
  },

  /* Video upload dropzones — stacked vertically for narrow inspector */
  videoDropzoneGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  videoDropzone: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px dashed rgba(255, 255, 255, 0.18)',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'rgba(255, 255, 255, 0.32)',
    },
  },
  videoDropzoneText: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: '2px',
  },
  videoDropzoneActive: {
    borderColor: COLORS.MYTAVERSE_BLUE,
    backgroundColor: 'rgba(69, 84, 229, 0.1)',
  },
  videoDropzoneIcon: {
    fontSize: 22,
    color: colors.white_40,
    flexShrink: 0,
  },
  videoDropzoneLabel: {
    fontSize: '12px',
    color: COLORS.WHITE,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  videoDropzoneAction: {
    fontSize: '11px',
    color: COLORS.MYTAVERSE_BLUE,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  videoDropzoneSubtitle: {
    fontSize: '11px',
    color: colors.white_50,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  videoDropzoneRequired: {
    color: '#FF6B6B',
  },
  videoConfirmRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  videoConfirmButton: {
    color: COLORS.WHITE,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    textTransform: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(69, 84, 229, 0.35)',
      color: 'rgba(255, 255, 255, 0.55)',
    },
  },

  distanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '0 4px',
  },
  distanceInfiniteToggle: {
    margin: 0,
    color: colors.white_60,
    '& .MuiTypography-root': {
      fontSize: '11px',
      fontWeight: 500,
      color: colors.white_60,
    },
    '& .MuiSwitch-root': {
      marginRight: '4px',
    },
  },
  infiniteLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  infiniteSymbol: {
    fontSize: '14px',
    fontWeight: 700,
    color: colors.white_60,
    lineHeight: 1,
  },
  infiniteText: {
    fontSize: '11px',
    fontWeight: 500,
    color: colors.white_60,
  },
  distanceSliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '0 6px',
  },
  distanceSlider: {
    flex: 1,
    color: COLORS.MYTAVERSE_BLUE,
    '& .MuiSlider-thumb': {
      backgroundColor: COLORS.WHITE,
    },
    '& .MuiSlider-rail': {
      opacity: 0.3,
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
    },
    '&.Mui-disabled': {
      color: 'rgba(255, 255, 255, 0.25)',
    },
  },
  distanceValue: {
    minWidth: 38,
    textAlign: 'right',
    color: COLORS.WHITE,
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  },

  assistantSection: {
    marginTop: '14px',
    padding: '10px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  assistantSectionText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  assistantSectionTitle: {
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 600,
  },
  assistantSectionSubtitle: {
    color: colors.white_50,
    fontSize: '11px',
  },
  assistantSectionButton: {
    color: COLORS.WHITE,
    borderColor: 'rgba(255, 255, 255, 0.2)',
    textTransform: 'none',
    padding: '4px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    minWidth: 'unset',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.35)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },

  /* Image lightbox */
  lightboxBackdrop: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.78)',
    },
  },
  lightboxImg: {
    maxWidth: '90vw',
    maxHeight: '88vh',
    objectFit: 'contain',
    borderRadius: '8px',
    display: 'block',
  },
  lightboxClose: {
    position: 'absolute',
    top: 14,
    right: 14,
    color: COLORS.WHITE,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
  },

  emptyHint: {
    color: colors.white_40,
    fontSize: '12px',
    padding: '12px 8px',
    textAlign: 'center',
    fontStyle: 'italic',
  },

  brandingEntityMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 10px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  brandingEntityMetaText: {
    color: colors.white_50,
    fontSize: '11px',
    wordBreak: 'break-word',
  },
  brandingPropertyRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '8px 10px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  brandingPropertyHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  brandingPropertyLabel: {
    flex: 1,
    minWidth: 0,
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  brandingPropertyTypeChip: {
    flexShrink: 0,
    color: colors.white_50,
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '2px 6px',
    borderRadius: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
  },
  brandingPropertyResetButton: {
    flexShrink: 0,
    padding: '2px',
    color: colors.white_50,
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
  brandingPropertyUploadRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  brandingPropertyHint: {
    color: colors.white_40,
    fontSize: '11px',
    wordBreak: 'break-all',
  },

  liveWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(255, 184, 0, 0.1)',
    border: '1px solid rgba(255, 184, 0, 0.32)',
    borderRadius: '8px',
    padding: '6px 10px',
    margin: '6px 0',
  },
  liveWarningText: {
    color: '#FFB800',
    fontSize: '11px',
    fontWeight: 500,
  },

  /* Map editor — the ordered room chain and its publish controls */
  mapEditorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    marginBottom: '6px',
  },
  // The cap. Dimmed and dashed to read as "part of the layout, not part of your list" —
  // it carries no controls, and a solid row with nothing to click looks broken instead.
  mapEditorEndRow: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderStyle: 'dashed',
    borderColor: 'rgba(255, 255, 255, 0.14)',
    opacity: 0.75,
  },
  // The entrance. Dashed like the cap, because it is the same kind of row — the layout's own,
  // outside the author's numbered list — but at full opacity and with a brighter border, because
  // unlike the cap it is a decision: it carries a control, and dimming the one row in the panel
  // that can be changed would say the opposite of what it is for.
  mapEditorStartRow: {
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    borderStyle: 'dashed',
    borderColor: 'rgba(255, 255, 255, 0.22)',
  },
  // Position in the chain, not a count — monospaced and tabular so the numbers stay
  // aligned as the list crosses from single to double digits while rooms are reordered.
  mapEditorRowIndex: {
    color: colors.white_40,
    fontSize: '11px',
    fontWeight: 700,
    minWidth: '18px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontVariantNumeric: 'tabular-nums',
  },
  // A grid of equal columns rather than a flex row. Flex was giving each action a different width:
  // its items floor at min-content, so every button's own label and padding pushed back against
  // flex-basis, and Save's wider padding made it the widest of the three. `minmax(0, 1fr)` has no
  // such floor, so the columns are equal by construction whatever the labels say — and auto-flow
  // means a fourth action would not need the count changed here.
  mapEditorFooter: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    gap: '8px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
    // The shared button styles set a min-width for standalone use; inside a fixed column it would be
    // the one thing able to overflow it.
    '& button': {
      minWidth: 0,
      width: '100%',
    },
  },
  mapEditorViewRow: {
    display: 'flex',
    marginTop: '12px',
    '& > *': {
      flex: 1,
    },
    '& button': {
      minWidth: 0,
      width: '100%',
    },
  },
  // For a footer action that needs a wrapper: a Tooltip has to hand its ref to a real element, and a
  // disabled MUI button fires no events for it to hook. Stretches so the button fills its column
  // instead of sizing to its label.
  mapEditorFooterAction: {
    display: 'flex',
  },
  mapEditorSaveButton: {
    flex: 1,
    color: COLORS.WHITE,
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    textTransform: 'none',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 600,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(69, 84, 229, 0.35)',
      color: 'rgba(255, 255, 255, 0.55)',
    },
  },
  // Unsaved is information, not a fault — it uses the brand blue rather than the amber
  // liveWarning treatment, which is reserved for things that are actually wrong.
  mapEditorUnsavedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(69, 84, 229, 0.12)',
    border: '1px solid rgba(69, 84, 229, 0.4)',
    borderRadius: '8px',
    padding: '6px 10px',
    margin: '10px 0 0',
  },
  mapEditorUnsavedText: {
    color: '#AEB6FF',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
  },
  // Red rather than the unsaved banner's indigo: this one reports that the world is not what the list
  // says it is, which is a different class of message from "not published yet".
  mapEditorRefusedBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    backgroundColor: 'rgba(255, 107, 107, 0.12)',
    border: '1px solid rgba(255, 107, 107, 0.4)',
    borderRadius: '8px',
    padding: '6px 10px',
    margin: '0 0 10px',
  },
  mapEditorRefusedText: {
    color: '#FFB3B3',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
  },
  mapEditorRefusedIcon: {
    fontSize: 14,
    color: '#FF6B6B',
    flexShrink: 0,
    marginTop: '1px',
  },

  // The same banner in amber, for a room the streamed build's kit cannot describe (MV-2004).
  // Deliberately not the refusal's red: a refusal is Unreal declining to build a room this build
  // has, which the author can often resolve by reordering, while this one is the wrong build for
  // the configuration and nothing in the walk will fix it. Matches the tree marker's amber, so the
  // row and the panel are recognisably about the same thing.
  mapEditorMissingBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    backgroundColor: 'rgba(245, 166, 35, 0.12)',
    border: '1px solid rgba(245, 166, 35, 0.4)',
    borderRadius: '8px',
    padding: '6px 10px',
    margin: '0 0 10px',
  },
  mapEditorMissingText: {
    color: '#F7CE8A',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
  },
  mapEditorMissingIcon: {
    fontSize: 14,
    color: '#F5A623',
    flexShrink: 0,
    marginTop: '1px',
  },

  // --- the segment panel ---
  //
  // Everything below is sized against the inspector's real 360px, which is what makes the
  // reorder/remove controls a row of their own rather than three icons crowded onto a tree row: a
  // panel drawn 60px wider would not have found that.
  mapEditorEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '14px 4px',
  },
  mapEditorEmptyIcon: {
    fontSize: 26,
    color: colors.white_20,
  },
  mapEditorEmptyTitle: {
    color: colors.white_70,
    fontSize: '13px',
    fontWeight: 600,
  },
  mapEditorSegmentHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 2px 0',
  },
  mapEditorSegmentText: {
    flex: 1,
    minWidth: 0,
  },
  mapEditorSegmentName: {
    color: COLORS.WHITE,
    fontSize: '13.5px',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mapEditorSegmentMeta: {
    color: colors.white_50,
    fontSize: '11px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mapEditorSegmentWarning: {
    fontSize: 15,
    color: '#FFB800',
    flexShrink: 0,
  },
  mapEditorSegmentChange: {
    height: '30px',
    padding: '0 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: COLORS.WHITE,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 0,
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  // The accent, because it is the only control here that moves the author rather than the layout —
  // and the one thing they cannot do from anywhere else in the editor.
  mapEditorTakeMeThere: {
    width: '100%',
    marginTop: '10px',
    height: '36px',
    borderRadius: '9px',
    border: '1px solid #5865E8',
    backgroundColor: COLORS.MYTAVERSE_BLUE,
    color: COLORS.WHITE,
    fontSize: '13px',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 6px 18px rgba(69, 84, 229, 0.35)',
    '&:hover': { backgroundColor: '#5865E8' },
  },
  // Quiet while the author is already walking. Still live rather than disabled: the same press moves
  // them from wherever they have wandered to back into this segment, which is a reasonable thing to
  // want a second time.
  mapEditorTakeMeThereActive: {
    backgroundColor: 'rgba(69, 84, 229, 0.22)',
    borderColor: 'rgba(69, 84, 229, 0.55)',
    boxShadow: 'none',
    '&:hover': { backgroundColor: 'rgba(69, 84, 229, 0.30)' },
  },
  // What became of the landing this panel asked for. Two notices rather than one, and the colours
  // carry the difference: waiting is quiet because it resolves in about 300ms and a red flash on
  // every successful walk would be worse than no notice at all, while a refusal is the thing that
  // used to be silent and has to read as a stop.
  mapEditorLandingPending: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '8px',
    padding: '5px 10px',
    marginTop: '6px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.10)',
  },
  mapEditorLandingFailed: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    borderRadius: '8px',
    padding: '5px 10px',
    marginTop: '6px',
    backgroundColor: 'rgba(255, 107, 107, 0.12)',
    border: '1px solid rgba(255, 107, 107, 0.4)',
  },
  mapEditorLandingText: {
    color: '#FFFFFFCC',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
  },
  mapEditorSegmentActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '8px',
  },
  mapEditorSegmentAction: {
    flex: 1,
    height: '30px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backgroundColor: 'transparent',
    color: colors.white_70,
    fontSize: '11.5px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    minWidth: 0,
    padding: '0 6px',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.28)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      color: COLORS.WHITE,
    },
    '&.Mui-disabled': {
      color: colors.white_20,
      borderColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  mapEditorSegmentRemove: {
    '&:hover': {
      borderColor: 'rgba(255, 107, 107, 0.45)',
      backgroundColor: 'rgba(255, 107, 107, 0.10)',
      color: '#FF6B6B',
    },
  },
  mapEditorSegmentDivider: {
    borderColor: 'rgba(255, 255, 255, 0.07)',
    margin: '14px 0 12px',
  },
  mapEditorFrames: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    padding: '0 2px',
  },
  mapEditorFramesHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '8px',
  },
  mapEditorFramesTitle: {
    color: COLORS.WHITE,
    fontSize: '12px',
    fontWeight: 600,
  },
  mapEditorFramesCount: {
    color: colors.white_50,
    fontSize: '11px',
    textAlign: 'right',
    minWidth: 0,
  },
  mapEditorFrameGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
  },
  // One frame of this segment's walls, showing whatever is on it.
  //
  // `overflow: hidden` is what the rounded corner is made of — the picture fills the tile edge to
  // edge, so without it the image's own square corners sit outside the border.
  mapEditorFrameTile: {
    width: '38px',
    aspectRatio: '4 / 3',
    borderRadius: '5px',
    border: '1px solid rgba(255, 255, 255, 0.14)',
    backgroundColor: 'rgba(120, 150, 200, 0.22)',
    flexShrink: 0,
    overflow: 'hidden',
  },
  // What makes a frame pressable, applied only to the frames Unreal can address one at a time.
  //
  // It looked pressable and was not for a whole release — the tile was a plain Box beside a hint
  // sending the author to the Spots section, which does not list a layout frame until content has
  // been put on it, so the only way in was to walk into the room and click the wall. A frame that
  // takes a picture is the section's whole point; the styling saying so is not decoration.
  //
  // Resets what the browser puts on a <button>: the base tile owns the border and the background,
  // and a default padding would inset the picture inside its own frame.
  mapEditorFrameTileButton: {
    padding: 0,
    display: 'block',
    cursor: 'pointer',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.45)',
    },
    '&:focus-visible': {
      outline: 'none',
      borderColor: '#5865E8',
      boxShadow: '0 0 0 2px rgba(69, 84, 229, 0.45)',
    },
  },
  // A frame the standing layout gives no id of its own, so there is nothing to open an inspector
  // on. Dimmed rather than hidden: it is a frame that exists on the wall in front of the author,
  // and leaving it out of the count would be the more confusing answer. The section's second hint
  // says what to do about it.
  mapEditorFrameTileInert: {
    opacity: 0.4,
  },
  // Fills its frame, cropping rather than letterboxing: at 38px a fitted image is mostly padding,
  // and the tile is here to be recognised at a glance rather than inspected.
  mapEditorFrameThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  mapEditorFramesHint: {
    color: colors.white_40,
    fontSize: '10.5px',
    lineHeight: 1.4,
  },
  mapEditorNote: {
    color: colors.white_50,
    fontSize: '11px',
    lineHeight: 1.5,
    marginTop: '10px',
    padding: '0 4px',
  },

  // Amber rather than red: an over-budget layout is a limit the author can work inside, not a fault.
  mapEditorOversizeBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    backgroundColor: 'rgba(255, 184, 0, 0.12)',
    border: '1px solid rgba(255, 184, 0, 0.4)',
    borderRadius: '8px',
    padding: '6px 10px',
    margin: '10px 0 0',
  },
  mapEditorOversizeText: {
    color: '#FFD980',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.35,
  },

  finishSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '14px',
    paddingTop: '12px',
    borderTop: `1px solid ${colors.white_10}`,
  },
  finishSectionTitle: {
    color: colors.white_50,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '0 4px',
  },
  finishRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 4px',
  },
  finishFieldLabel: {
    color: colors.white_60,
    fontSize: '11px',
    fontWeight: 500,
    flexShrink: 0,
  },
  finishFieldValue: {
    color: colors.white,
    fontSize: '11px',
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  finishSelect: {
    flex: 1,
    fontSize: '11px',
    '& .MuiSelect-select': { padding: '5px 8px' },
  },
  finishSlotGroup: {
    padding: '0 4px',
    flexWrap: 'wrap',
  },
  finishSlotButton: {
    color: colors.white_60,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    padding: '3px 10px',
    borderColor: colors.white_10,
    '&.Mui-selected': {
      color: colors.white,
      backgroundColor: 'rgba(69, 84, 229, 0.35)',
    },
  },
  finishScopeHint: {
    color: '#AEB6FF',
    fontSize: '10px',
    lineHeight: 1.4,
    padding: '0 4px',
  },
  // Spreads this surface's finish to the layout's default. Full width and quiet: it is the step after
  // the choice, and it repaints every segment — which is worth reading before pressing rather than
  // sitting under the accent that says "press me".
  finishApplyEverywhere: {
    width: '100%',
    marginTop: '10px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: COLORS.WHITE,
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.32)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&.Mui-disabled': {
      color: colors.white_20,
      borderColor: 'rgba(255, 255, 255, 0.10)',
    },
  },
  // Two to a row: the tile carries a name and a line of parameters, and one column of them would
  // push the controls below off the panel.
  finishTileGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '6px',
    padding: '0 4px',
  },
  finishTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    alignItems: 'flex-start',
    textAlign: 'left',
    cursor: 'pointer',
    padding: '7px 8px',
    borderRadius: '8px',
    border: `1px solid ${colors.white_10}`,
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    '&:hover': { borderColor: 'rgba(69, 84, 229, 0.6)' },
  },
  finishTileSelected: {
    borderColor: '#4554E5',
    backgroundColor: 'rgba(69, 84, 229, 0.22)',
  },
  // Still legible, and still saying what it is for: the subtitle carries the reason it cannot be
  // chosen, so fading it out of readability would take that with it.
  finishTileDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  finishTileImage: {
    width: '100%',
    height: '44px',
    objectFit: 'cover',
    borderRadius: '5px',
  },
  finishTileTitle: {
    color: colors.white,
    fontSize: '11px',
    fontWeight: 600,
    lineHeight: 1.25,
  },
  finishTileDiff: {
    color: colors.white_50,
    fontSize: '9px',
    lineHeight: 1.3,
  },
  finishTuningHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 4px 0',
    borderTop: `1px solid ${colors.white_10}`,
    marginTop: '4px',
  },
  finishTuningChanged: {
    color: '#AEB6FF',
    fontSize: '10px',
    fontWeight: 500,
    flex: 1,
  },
  finishTuningToggle: {
    color: colors.white_60,
    padding: '2px',
    marginLeft: 'auto',
    '&:hover': { color: '#FFFFFF' },
  },
  finishGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '8px 4px 4px',
    borderTop: `1px solid ${colors.white_10}`,
  },
  finishGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '6px',
  },
  finishGroupTitle: {
    color: colors.white_60,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  finishResetButton: {
    color: colors.white_50,
    fontSize: '10px',
    minWidth: 0,
    padding: '1px 6px',
  },
  // The two whole-finish actions, pushed apart: one restores, one removes, and they should not read
  // as a pair of equals sitting side by side.
  finishActionsRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '0 4px',
  },
  finishResetAllButton: {
    color: colors.white_60,
    fontSize: '10px',
    minWidth: 0,
    padding: '1px 6px',
  },
  finishClearButton: {
    color: '#FFB3B3',
    fontSize: '10px',
    minWidth: 0,
    padding: '1px 6px',
    flexShrink: 0,
  },
  finishControl: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  finishControlHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  // Holds the label and its hint icon together, so the icon sits against the end of the text rather
  // than out by the value. Takes the row's spare width in the label's place; minWidth lets the text
  // inside it still shrink to an ellipsis.
  finishControlLabelGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    minWidth: 0,
  },
  finishControlLabel: {
    color: colors.white_60,
    fontSize: '11px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Dimmer than the label it follows: it has to be findable while reading a row, and not compete
  // with the seventeen values the eye is actually scanning.
  finishControlHintIcon: {
    fontSize: '12px',
    color: colors.white_40,
    flexShrink: 0,
    cursor: 'help',
  },
  finishControlValue: {
    color: colors.white,
    fontSize: '11px',
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
  // The one mark that says a value is no longer the finish's own. Small on purpose: it appears on up
  // to seventeen rows at once, and a badge on each would read as noise rather than as information.
  finishModifiedDot: {
    width: '5px',
    height: '5px',
    borderRadius: '50%',
    backgroundColor: '#AEB6FF',
    flexShrink: 0,
  },
  finishSlider: {
    padding: '6px 0',
    '& .MuiSlider-thumb': { width: 11, height: 11 },
  },
  finishConstraintHint: {
    color: '#FFD980',
    fontSize: '9px',
    lineHeight: 1.3,
  },
  finishSwatch: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    border: `1px solid ${colors.white_20}`,
    flexShrink: 0,
  },
  finishColorInput: {
    width: '100%',
    height: '22px',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  },
});
